<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";
import { generatePDF } from "@/common/utils/pdf";

import { analysisReport } from "@/common/api/rest/analysis/analysis";

import type { AnalysisFullType } from "@/common/types/analysis";

import { sidebarListCheckbox, svgIcon } from "@/common/components";
import { viewReportAnalyse } from "@/components";

const { t } = useI18n();
const route = useRoute();

const props = defineProps<{
  accountId: string;
  analysisId: string;
}>();

const width = ref("210mm");
const analyse = ref<AnalysisFullType | null>(null);
const key = ref(0);

const added = ref<string[]>([
  "company",
  "analyse",
  "employee",
  "method",
  "sample",
  "equipment",
  "signature",
]);

const showFilter = ref(false);

const accountId = computed(() => route.params.id as string);

const list = computed(() => [
  {
    key: "all",
    label: "Selecionar Tudo",
  },
  {
    key: "company",
    label: "Dados da Empresa",
  },
  {
    key: "analyse",
    label: "Dados da Análise",
  },
  {
    key: "employee",
    label: "Funcionário",
  },
  {
    key: "method",
    label: "Métodos",
  },
  {
    key: "form_question",
    label: "Perguntas dos Formulários",
  },
  {
    key: "method_question",
    label: "Perguntas dos Métodos",
  },
  {
    key: "sample",
    label: "Amostras",
  },
  {
    key: "equipment",
    label: "Equipamentos",
  },
  {
    key: "signature",
    label: "Assinaturas",
  },
]);

const scale = computed(() => {
  const width = window.innerWidth;

  if (store.getters.reportScale) return 1;

  if (width >= 1444) return 1;

  if (width >= 1366 && width < 1444) return 0.875;

  if (width >= 1024 && width < 1366) return 0.75;

  if (width > 800 && width < 1024) return 0.75;

  if (width <= 800) return 0.47;

  return 1;
});

async function fetchData() {
  if (!props.analysisId) return;

  await analysisReport(accountId.value, props.analysisId)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        analyse.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function printPage() {
  store.commit("SET_REPORT_SCALE", true);
  store.commit("SET_LOADING", true);
  store.commit("SET_PRINT", true);

  const element = document.getElementById("reportView");
  if (!element) {
    store.commit("SET_REPORT_SCALE", null);
    store.commit("SET_LOADING", false);
    return;
  }

  await new Promise((resolve) => {
    setTimeout(async () => {
      resolve(true);
    }, 1000);
  });

  await generatePDF(element, `analyse-${analyse.value?.code}.pdf`);

  setTimeout(async () => {
    store.commit("SET_REPORT_SCALE", null);
    store.commit("SET_LOADING", false);
    store.commit("SET_PRINT", false);
  }, 1000);
}

function openFilter() {
  showFilter.value = true;
}

function closeFilter() {
  showFilter.value = false;
}

function addFilter(item: any) {
  if (added.value.includes(item.key)) {
    return;
  }

  if (item.key === "all") {
    added.value = list.value.map((i) => i.key);
    key.value += 1;
    return;
  }

  added.value.push(item.key);

  const allKey = list.value.map((i) => i.key).filter((key) => key !== "all");
  if (added.value.length === allKey.length) {
    added.value.push("all");
  }

  key.value += 1;
}

function removeFilter(item: any) {
  if (item.key === "all") {
    added.value = [];
    key.value += 1;
    return;
  }

  added.value = added.value
    .filter((key) => key !== item.key)
    .filter((key) => key !== "all");

  key.value += 1;
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <article
    class="analysis-report"
    :class="{
      print: $store.getters.reportScale,
    }"
  >
    <section class="actions">
      <button type="button" class="btn btn btn-default" @click="openFilter()">
        <svg-icon icon="filter" size="1.5rem" />
        {{ t("common.labels.filters") }}
      </button>

      <button type="button" class="btn btn btn-default" @click="printPage()">
        <svg-icon icon="download" size="1.5rem" />
        {{ t("common.labels.download") }}
      </button>
    </section>

    <section id="reportView" class="content">
      <template v-if="analyse">
        <view-report-analyse
          :key="key"
          :data="analyse"
          :scale="scale"
          :width="width"
          :filters="added"
        />
      </template>
    </section>

    <template v-if="showFilter">
      <sidebar-list-checkbox
        id="franchise"
        :width="$store.getters.mobile ? '90vw' : '40rem'"
        :open="showFilter"
        :outside-close="true"
        :title="t('common.labels.filters')"
        :list="list"
        field-id="key"
        field-option="label"
        :added="added"
        @close="closeFilter()"
        @add:item="addFilter"
        @remove:item="removeFilter"
      />
    </template>
  </article>
</template>

<style lang="scss" scoped>
$width: calc(v-bind("width") * v-bind("scale"));
$height: calc((v-bind("width") * 1.41428) * v-bind("scale"));
$fontSize: calc(1rem * v-bind("scale"));

.analysis-report {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: white;

  .actions {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    align-items: center;
    justify-content: center;
    padding-block: 1.5rem;
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  .page {
    display: flex;
    flex-direction: column;
    width: $width;
    height: $height;
    max-height: $height;
    font-size: $fontSize;
    page-break-after: always;
    border: 1px solid var(--border);

    .content {
      flex: 1;
    }

    .footer {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.15em;
      width: 90%;
      margin-inline: auto;
      padding-block-start: 0.25em;
      border-block-start: 1px solid var(--border);

      .title {
        font-size: 0.9em;
        text-transform: uppercase;
      }

      .subtitle {
        font-size: 0.75em;
      }
    }

    .page-number {
      display: flex;
      width: 90%;
      margin-inline: auto;
      height: 1.5rem;
      flex-direction: row;
      align-items: flex-start;
      justify-content: flex-end;
      font-size: 0.7rem;
    }
  }

  &.print {
    gap: 0;

    .page {
      border: 0px;
      font-size: 1rem;
      width: 210mm;
      height: 297mm;
    }
  }
}

@page {
  size: A4;
  margin: 0mm;
}

@media print {
  body {
    background: white !important;
  }

  body * {
    visibility: hidden !important;
  }

  #reportView,
  #reportView * {
    visibility: visible !important;
  }

  #reportView {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    z-index: 999;
    background: white !important;
  }
}
</style>
